import React, { useEffect, useState } from 'react';
import { Plus, X } from 'lucide-react';
import api from '../services/api';
import AutocompleteProducto from './AutocompleteProducto';
import { formatMonto, hoyCaracas } from '../utils/format';
import { pref, MONEDA_LABEL } from '../utils/moneda';

// ============================================================
// Alta/edición de una plantilla de factura recurrente (#140), ej. el
// alquiler de todos los meses. Las líneas usan el mismo shape que
// FacturaForm (producto o categoría + cantidad + precio + exento), pero
// acá no hay carga por IA ni bloque fiscal: eso se decide recién al
// confirmar una ocurrencia (modo recordatorio) o nunca se fiscaliza (modo
// automático, siempre no fiscal — ver facturasRecurrentesService.js).
// ============================================================

const DIAS_SEMANA = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const LINEA_VACIA = { producto_id: null, categoria_id: '', descripcion: '', cantidad: '1', precio_unitario: '', exento: false };

export default function FacturaRecurrenteForm({ plantillaId = null, onGuardado, onCancelar }) {
  const [proveedores, setProveedores] = useState([]);
  const [productos, setProductos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    let vivo = true;
    (async () => {
      const [prov, prod, cat] = await Promise.all([
        api.get('/proveedores'), api.get('/productos'), api.get('/categorias')
      ]);
      if (!vivo) return;
      setProveedores(prov.data.filter((p) => p.activo));
      setProductos(prod.data);
      setCategorias(cat.data);

      if (plantillaId) {
        const { data: d } = await api.get(`/compras/recurrentes/${plantillaId}`);
        if (!vivo) return;
        setForm({
          proveedor_id: d.proveedor_id, nombre: d.nombre, modo: d.modo, moneda: d.moneda,
          dias_credito: d.dias_credito ?? '',
          intervalo_tipo: d.intervalo_tipo, intervalo_cada: String(d.intervalo_cada),
          dia_mes: d.dia_mes ?? '1', dia_semana: d.dia_semana ?? '1',
          fecha_inicio: String(d.fecha_inicio).slice(0, 10),
          fecha_fin: d.fecha_fin ? String(d.fecha_fin).slice(0, 10) : '',
          dias_anticipacion: d.dias_anticipacion ?? 0,
          notas: d.notas || '',
          lineas: d.lineas.length
            ? d.lineas.map((l) => ({
                producto_id: l.producto_id, categoria_id: l.categoria_id || '',
                descripcion: l.descripcion, cantidad: String(l.cantidad),
                precio_unitario: String(l.precio_unitario), exento: !!l.exento
              }))
            : [{ ...LINEA_VACIA }]
        });
        return;
      }

      setForm({
        proveedor_id: '', nombre: '', modo: 'automatico', moneda: 'USD', dias_credito: '',
        intervalo_tipo: 'mensual', intervalo_cada: '1', dia_mes: '1', dia_semana: '1',
        fecha_inicio: hoyCaracas(), fecha_fin: '', dias_anticipacion: 0, notas: '',
        lineas: [{ ...LINEA_VACIA }]
      });
    })().catch((err) => setError(err.response?.data?.error || 'No se pudo abrir el formulario'));
    return () => { vivo = false; };
  }, [plantillaId]);

  function setLinea(i, cambios) {
    setForm((f) => ({ ...f, lineas: f.lineas.map((l, idx) => (idx === i ? { ...l, ...cambios } : l)) }));
  }
  const agregarLinea = () => setForm((f) => ({ ...f, lineas: [...f.lineas, { ...LINEA_VACIA }] }));
  const quitarLinea = (i) => setForm((f) => ({ ...f, lineas: f.lineas.filter((_, idx) => idx !== i) }));

  function elegirProducto(i, prodId) {
    const p = productos.find((x) => String(x.id) === String(prodId));
    setLinea(i, { producto_id: prodId || null, descripcion: prodId ? (p?.nombre || '') : '' });
  }

  async function guardar(e) {
    e.preventDefault();
    setError('');
    setGuardando(true);
    const payload = {
      proveedor_id: form.proveedor_id, nombre: form.nombre, modo: form.modo, moneda: form.moneda,
      dias_credito: form.dias_credito || null,
      intervalo_tipo: form.intervalo_tipo, intervalo_cada: form.intervalo_cada,
      dia_mes: form.intervalo_tipo === 'mensual' ? form.dia_mes : null,
      dia_semana: form.intervalo_tipo === 'semanal' ? form.dia_semana : null,
      fecha_inicio: form.fecha_inicio, fecha_fin: form.fecha_fin || null,
      dias_anticipacion: form.dias_anticipacion, notas: form.notas,
      lineas: form.lineas.map((l) => ({
        producto_id: l.producto_id || null, categoria_id: l.categoria_id || null,
        descripcion: l.descripcion, cantidad: l.cantidad, precio_unitario: l.precio_unitario, exento: !!l.exento
      }))
    };
    try {
      const { data } = plantillaId
        ? await api.put(`/compras/recurrentes/${plantillaId}`, payload)
        : await api.post('/compras/recurrentes', payload);
      onGuardado?.({ ...data, nombre: form.nombre, modo: form.modo });
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo guardar la plantilla');
    } finally {
      setGuardando(false);
    }
  }

  if (!form) return <p className="text-sm text-slate-400 py-6 text-center">{error || 'Cargando…'}</p>;

  return (
    <form onSubmit={guardar} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className="block text-xs text-slate-500 mb-1">Nombre de la plantilla</label>
          <input
            required autoFocus
            placeholder='Ej. "Alquiler Capricciosa"'
            value={form.nombre}
            onChange={(e) => setForm({ ...form, nombre: e.target.value })}
            className="border rounded-md px-3 py-2 w-full"
          />
        </div>

        <div>
          <label className="block text-xs text-slate-500 mb-1">Proveedor</label>
          <select
            required
            value={form.proveedor_id}
            onChange={(e) => setForm({ ...form, proveedor_id: e.target.value })}
            className="border rounded-md px-3 py-2 w-full"
          >
            <option value="">Elegir...</option>
            {proveedores.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-xs text-slate-500 mb-1">Modo</label>
          <select
            value={form.modo}
            onChange={(e) => setForm({ ...form, modo: e.target.value })}
            className="border rounded-md px-3 py-2 w-full"
          >
            <option value="automatico">Automático (se crea sola, no fiscal)</option>
            <option value="recordatorio">Recordatorio (solo avisa; tú ajustas monto y número)</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-slate-500 mb-1">Moneda</label>
          <select
            value={form.moneda}
            onChange={(e) => setForm({ ...form, moneda: e.target.value })}
            className="border rounded-md px-3 py-2 w-full"
          >
            <option value="USD">USD</option>
            <option value="BCV">BCV</option>
            <option value="BS">BS</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-slate-500 mb-1">Días de crédito (opcional)</label>
          <input
            type="number" min="0"
            value={form.dias_credito}
            onChange={(e) => setForm({ ...form, dias_credito: e.target.value })}
            className="border rounded-md px-3 py-2 w-full"
          />
        </div>

        <div>
          <label className="block text-xs text-slate-500 mb-1">Repetir</label>
          <div className="grid grid-cols-2 gap-2">
            <select
              value={form.intervalo_tipo}
              onChange={(e) => setForm({ ...form, intervalo_tipo: e.target.value })}
              className="border rounded-md px-3 py-2 w-full"
            >
              <option value="mensual">Mensual</option>
              <option value="semanal">Semanal</option>
            </select>
            <input
              type="number" min="1" title="Cada cuántos meses/semanas"
              value={form.intervalo_cada}
              onChange={(e) => setForm({ ...form, intervalo_cada: e.target.value })}
              className="border rounded-md px-3 py-2 w-full"
            />
          </div>
        </div>

        {form.intervalo_tipo === 'mensual' ? (
          <div>
            <label className="block text-xs text-slate-500 mb-1">Día del mes</label>
            <input
              type="number" min="1" max="31"
              value={form.dia_mes}
              onChange={(e) => setForm({ ...form, dia_mes: e.target.value })}
              className="border rounded-md px-3 py-2 w-full"
            />
            <p className="text-xs text-slate-400 mt-1">31 en un mes más corto cae en su último día.</p>
          </div>
        ) : (
          <div>
            <label className="block text-xs text-slate-500 mb-1">Día de la semana</label>
            <select
              value={form.dia_semana}
              onChange={(e) => setForm({ ...form, dia_semana: e.target.value })}
              className="border rounded-md px-3 py-2 w-full"
            >
              {DIAS_SEMANA.map((d, i) => <option key={i} value={i}>{d}</option>)}
            </select>
          </div>
        )}

        <div>
          <label className="block text-xs text-slate-500 mb-1">Fecha de inicio</label>
          <input
            type="date" required
            value={form.fecha_inicio}
            onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })}
            className="border rounded-md px-3 py-2 w-full"
          />
        </div>

        <div>
          <label className="block text-xs text-slate-500 mb-1">Fecha de fin (opcional)</label>
          <input
            type="date"
            value={form.fecha_fin}
            min={form.fecha_inicio}
            onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })}
            className="border rounded-md px-3 py-2 w-full"
          />
        </div>

        <div>
          <label className="block text-xs text-slate-500 mb-1">
            {form.modo === 'automatico' ? 'Crear con' : 'Avisar con'} cuántos días de anticipación
          </label>
          <input
            type="number" min="0"
            value={form.dias_anticipacion}
            onChange={(e) => setForm({ ...form, dias_anticipacion: e.target.value })}
            className="border rounded-md px-3 py-2 w-full"
          />
        </div>

        <div className="sm:col-span-2">
          <label className="block text-xs text-slate-500 mb-1">Notas (opcional)</label>
          <input
            value={form.notas}
            onChange={(e) => setForm({ ...form, notas: e.target.value })}
            className="border rounded-md px-3 py-2 w-full"
          />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs text-slate-500">Líneas (precios en {MONEDA_LABEL[form.moneda]})</label>
          <button type="button" onClick={agregarLinea} title="Agregar línea" className="text-slate-400 hover:text-primary-600 transition-colors">
            <Plus size={16} />
          </button>
        </div>
        <div className="hidden sm:grid grid-cols-12 gap-2 px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          <div className="col-span-3">Producto</div>
          <div className="col-span-4">Descripción</div>
          <div className="col-span-1 text-right">Cant.</div>
          <div className="col-span-2 text-right">Precio</div>
          <div className="col-span-2 text-right">Monto</div>
        </div>
        <div className="space-y-2">
          {form.lineas.map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-start rounded-md p-2 bg-slate-50">
              <div className="col-span-12 sm:col-span-3">
                <AutocompleteProducto
                  productos={productos}
                  value={l.producto_id}
                  onChange={(pid) => elegirProducto(i, pid)}
                  placeholder="Producto (o dejar vacío)"
                />
              </div>
              <div className="col-span-12 sm:col-span-4">
                <input
                  value={l.descripcion}
                  onChange={(e) => setLinea(i, { descripcion: e.target.value })}
                  placeholder="Descripción"
                  className="border rounded-md px-2 py-1.5 w-full text-sm"
                />
                {!l.producto_id && (
                  <select
                    value={l.categoria_id}
                    onChange={(e) => setLinea(i, { categoria_id: e.target.value })}
                    className="border rounded-md px-2 py-1.5 w-full text-sm mt-1"
                  >
                    <option value="">Categoría del gasto...</option>
                    {categorias.filter((c) => c.tipo === 'egreso').map((c) => (
                      <option key={c.id} value={c.id}>{c.codigo ? `${c.codigo} ` : ''}{c.padre_nombre ? `${c.padre_nombre} › ` : ''}{c.nombre}</option>
                    ))}
                  </select>
                )}
              </div>
              <div className="col-span-3 sm:col-span-1">
                <input
                  type="number" step="0.0001" min="0"
                  value={l.cantidad}
                  onChange={(e) => setLinea(i, { cantidad: e.target.value })}
                  placeholder="Cant."
                  className="border rounded-md px-2 py-1.5 w-full text-sm text-right"
                />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <input
                  type="number" step="0.0001" min="0"
                  value={l.precio_unitario}
                  onChange={(e) => setLinea(i, { precio_unitario: e.target.value })}
                  placeholder="Precio"
                  className="border rounded-md px-2 py-1.5 w-full text-sm text-right"
                />
              </div>
              <div className="col-span-5 sm:col-span-2 flex items-center justify-end gap-2 pt-1.5">
                <span className="text-sm text-slate-700">
                  {pref(form.moneda)}{formatMonto((Number(l.cantidad) || 0) * (Number(l.precio_unitario) || 0))}
                </span>
                {form.lineas.length > 1 && (
                  <button type="button" onClick={() => quitarLinea(i)} className="text-slate-400 hover:text-red-600" title="Quitar línea">
                    <X size={15} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      <button type="submit" disabled={guardando} className="bg-primary-600 text-white rounded-md py-2 w-full hover:bg-primary-700 disabled:opacity-50">
        {plantillaId ? 'Guardar cambios' : 'Guardar'}
      </button>
    </form>
  );
}
